import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "./Nav";

const HEADLINE = [
  ["Design que", false],
  ["revela a sua", false],
  ["essência.", true],
];

function RevealLine({ text, accent, index }) {
  return (
    <span className="hero-line-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay: 0.15 + index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className={accent ? "text-butter" : ""}>{text}</span>
      </motion.span>
    </span>
  );
}

export function TiltBadge() {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const ry = ((e.clientX - rect.left) / rect.width - 0.5) * 14;
    const rx = -((e.clientY - rect.top) / rect.height - 0.5) * 14;
    setTilt({ rx, ry });
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
      style={{ perspective: "800px" }}
      className="absolute -top-6 right-4 z-10 sm:-right-6 sm:top-10"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
          transition: "transform 0.2s ease-out",
          transformStyle: "preserve-3d",
        }}
        data-testid="hero-price-badge"
        className="rounded-2xl border border-line bg-white/90 px-6 py-5 shadow-[0_32px_60px_-28px_rgba(17,17,17,0.35)] backdrop-blur"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3">A partir de</p>
        <p className="font-display text-3xl font-extrabold tracking-tight">R$ 40</p>
        <p className="mt-1 text-xs text-ink-2">entrega em dias úteis</p>
      </motion.div>
    </div>
  );
}

export default function Hero({ onOrder }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section ref={ref} className="relative flex min-h-[92vh] items-center overflow-hidden pt-24" data-testid="hero-section">
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-14 px-6 py-12 sm:px-12 lg:grid-cols-2 lg:gap-10 lg:px-20">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-3"
          >
            Studio de design gráfico · @essenzaabr
          </motion.p>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            {HEADLINE.map(([text, accent], i) => (
              <RevealLine key={text} text={text} accent={accent} index={i} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 max-w-md text-base leading-relaxed text-ink-2 sm:text-lg"
          >
            Identidades visuais, kits para redes sociais e artes sob encomenda — com entrega em dias, direto do estúdio para o seu negócio.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-order-cta-button"
              onClick={onOrder}
              className="btn-lift rounded-full bg-butter px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-paper"
            >
              Encomendar agora
            </button>
            <button
              data-testid="hero-services-link-button"
              onClick={() => scrollToId("servicos")}
              className="btn-lift flex items-center gap-2 rounded-full border border-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-paper"
            >
              Ver serviços <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <TiltBadge />
          <div className="overflow-hidden rounded-3xl border border-line bg-paper3">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src="https://images.unsplash.com/photo-1520764588094-8fc067746a8e?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Trabalho de design gráfico do estúdio Essenza"
              className="h-[420px] w-full object-cover sm:h-[520px]"
              data-testid="hero-image"
            />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-3">
            Essenza — identidades visuais essenciais
          </p>
        </motion.div>
      </div>
    </section>
  );
}
